import React, { useState } from 'react';
import { FaArrowLeft, FaCheck, FaUserAlt, FaRuler, FaWeight, FaPalette } from 'react-icons/fa';
import { toast } from 'react-toastify';

const AppearanceDetails = ({ formData, onSubmit, onPrevious, loading }) => {
    const [localData, setLocalData] = useState({
        height: formData.height || '',
        weight: formData.weight || '',
        skin_tone: formData.skin_tone || ''
    });
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    
    // Generate height options with numeric values
    const generateHeightOptions = () => {
        const heights = [];
        for (let ft = 4; ft <= 6; ft++) {
            for (let inch = 0; inch <= 11; inch++) {
                const totalInches = (ft * 12) + inch;
                const displayValue = `${ft}ft ${inch}in`;
                heights.push({ value: totalInches, label: displayValue });
            }
        }
        return heights;
    };
    
    // Generate weight options (from 30kg to 150kg)
    const generateWeightOptions = () => {
        const weights = [];
        for (let kg = 30; kg <= 150; kg++) {
            weights.push({ value: kg, label: `${kg} kg` });
        }
        return weights;
    };
    
    const heightOptions = generateHeightOptions();
    const weightOptions = generateWeightOptions();
    
    const handleChange = (e) => {
        const { name, value } = e.target;
        setLocalData({ ...localData, [name]: value });
        
        // Mark field as touched
        setTouched({ ...touched, [name]: true });
        
        // Clear error for this field
        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    };
    
    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched({ ...touched, [name]: true });
        validateField(name, localData[name]);
    };
    
    const validateField = (fieldName, value) => {
        let error = '';
        
        switch (fieldName) {
            case 'height':
                if (!value || value === '') {
                    error = 'Height is required';
                } else if (parseInt(value) < 48) { // 4ft = 48 inches
                    error = 'Height must be at least 4ft';
                } else if (parseInt(value) > 96) { // 8ft = 96 inches
                    error = 'Height cannot exceed 8ft';
                }
                break;
            case 'weight':
                if (!value || value === '') {
                    error = 'Weight is required';
                } else if (parseInt(value) < 30) {
                    error = 'Weight must be at least 30 kg';
                } else if (parseInt(value) > 200) {
                    error = 'Weight cannot exceed 200 kg';
                }
                break;
            case 'skin_tone':
                if (!value || value === '') {
                    error = 'Skin tone is required';
                }
                break;
            default:
                break;
        }
        
        setErrors(prev => ({ ...prev, [fieldName]: error }));
        return error === '';
    };
    
    const validateForm = () => {
        const fieldsToValidate = ['height', 'weight', 'skin_tone'];
        let isValid = true;
        const newErrors = {};
        
        fieldsToValidate.forEach(field => {
            const value = localData[field];
            let error = '';
            
            switch (field) {
                case 'height':
                    if (!value || value === '') {
                        error = 'Height is required';
                    } else if (parseInt(value) < 48) {
                        error = 'Height must be at least 4ft';
                    } else if (parseInt(value) > 96) {
                        error = 'Height cannot exceed 8ft';
                    }
                    break;
                case 'weight':
                    if (!value || value === '') {
                        error = 'Weight is required';
                    } else if (parseInt(value) < 30) {
                        error = 'Weight must be at least 30 kg';
                    } else if (parseInt(value) > 200) {
                        error = 'Weight cannot exceed 200 kg';
                    }
                    break;
                case 'skin_tone':
                    if (!value || value === '') {
                        error = 'Skin tone is required';
                    }
                    break;
                default:
                    break;
            }
            
            if (error) {
                isValid = false;
                newErrors[field] = error;
            }
        });
        
        setErrors(newErrors);
        
        // Mark all fields as touched
        const allTouched = {};
        fieldsToValidate.forEach(field => {
            allTouched[field] = true;
        });
        setTouched(allTouched);
        
        return isValid;
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            // Convert height to numeric value (inches)
            let heightValue = localData.height;
            if (localData.height && !isNaN(localData.height)) {
                heightValue = parseInt(localData.height);
            }
            
            // Convert weight to numeric value
            let weightValue = localData.weight;
            if (localData.weight && !isNaN(localData.weight)) {
                weightValue = parseInt(localData.weight);
            }
            
            const submissionData = {
                height: heightValue,
                weight: weightValue,
                skin_tone: localData.skin_tone
            };
            
            console.log('Submitting appearance data:', submissionData);
            onSubmit(submissionData);
        } else {
            toast.error('Please fill all required fields correctly');
        }
    };
    
    // Get selected height label for display
    const getSelectedHeightLabel = () => {
        const selected = heightOptions.find(option => option.value == localData.height);
        return selected ? selected.label : 'Not selected';
    };
    
    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex items-center space-x-2 mb-6">
                <FaUserAlt className="text-indigo-600 text-xl" />
                <h2 className="text-2xl font-bold text-gray-800">Appearance Details</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Height Dropdown */}
                <div>
                    <label className="form-label">
                        <FaRuler className="inline mr-2" />
                        Height <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="height"
                        value={localData.height}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.height && touched.height ? 'input-error' : ''}`}
                    >
                        <option value="">Select Height</option>
                        {heightOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {errors.height && touched.height && <p className="error-message">{errors.height}</p>}
                    <p className="text-xs text-gray-500 mt-1">Example: 5ft 5in (65 inches)</p>
                </div>
                
                {/* Weight Dropdown */}
                <div>
                    <label className="form-label">
                        <FaWeight className="inline mr-2" />
                        Weight <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="weight"
                        value={localData.weight}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.weight && touched.weight ? 'input-error' : ''}`}
                    >
                        <option value="">Select Weight</option>
                        {weightOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {errors.weight && touched.weight && <p className="error-message">{errors.weight}</p>}
                    <p className="text-xs text-gray-500 mt-1">Select your weight in kilograms</p>
                </div>
                
                {/* Skin Tone Dropdown */}
                <div className="md:col-span-2">
                    <label className="form-label">
                        <FaPalette className="inline mr-2" />
                        Skin Tone <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="skin_tone"
                        value={localData.skin_tone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.skin_tone && touched.skin_tone ? 'input-error' : ''}`}
                    >
                        <option value="">Select Skin Tone</option>
                        <option value="fair">Fair</option>
                        <option value="wheatish">Wheatish</option>
                        <option value="medium">Medium</option>
                        <option value="olive">Olive</option>
                        <option value="brown">Brown</option>
                        <option value="dark">Dark</option>
                        <option value="very fair">Very Fair</option>
                    </select>
                    {errors.skin_tone && touched.skin_tone && <p className="error-message">{errors.skin_tone}</p>}
                </div>
            </div>
            
            {/* Summary Section */}
            <div className="mt-8 p-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl border border-indigo-100">
                <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center">
                    <FaCheck className="text-green-600 mr-2" />
                    Registration Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                        <p><strong className="text-gray-700">Name:</strong> <span className="text-gray-600">{formData.name || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Date of Birth:</strong> <span className="text-gray-600">{formData.dob || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Gender:</strong> <span className="text-gray-600">{formData.gender || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Marital Status:</strong> <span className="text-gray-600">{formData.marital || 'Not provided'}</span></p>
                    </div>
                    <div className="space-y-2">
                        <p><strong className="text-gray-700">Food Preference:</strong> <span className="text-gray-600">{formData.eat_status || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Caste:</strong> <span className="text-gray-600">{formData.caste || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Gotra:</strong> <span className="text-gray-600">{formData.gotra || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Registration Amount:</strong> <span className="text-green-600 font-semibold">Rs 4,000</span></p>
                    </div>
                </div>
                <div className="mt-4 pt-4 border-t border-indigo-200">
                    <div className="grid grid-cols-2 gap-4">
                        <p><strong className="text-gray-700">Selected Height:</strong> <span className="text-gray-600">{getSelectedHeightLabel()}</span></p>
                        <p><strong className="text-gray-700">Selected Weight:</strong> <span className="text-gray-600">{localData.weight ? `${localData.weight} kg` : 'Not selected'}</span></p>
                        <p><strong className="text-gray-700">Skin Tone:</strong> <span className="text-gray-600">{localData.skin_tone || 'Not selected'}</span></p>
                    </div>
                </div>
            </div>
            
            <div className="flex justify-between pt-4">
                <button
                    type="button"
                    onClick={onPrevious}
                    disabled={loading}
                    className="btn-secondary flex items-center space-x-2"
                >
                    <FaArrowLeft />
                    <span>Previous</span>
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary flex items-center space-x-2"
                >
                    {loading ? (
                        <>
                            <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin"></div>
                            <span>Saving...</span>
                        </>
                    ) : (
                        <>
                            <span>Next: Contact Information</span>
                            <span>→</span>
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default AppearanceDetails;